import { useEffect, useState } from 'react'

export interface IRowSelection {
  /** Os ids marcados **que estão na tela**, na ordem em que as linhas aparecem. */
  selectedIds: number[]
  /** Quantos estão marcados. É o número que a `<BulkActionBar>` escreve. */
  count: number
  isSelected: (id: number) => boolean
  toggle: (id: number) => void
  /** Marca todas as linhas selecionáveis da página, ou desmarca todas se já estiverem. */
  toggleAll: () => void
  /** Todas as linhas selecionáveis da página estão marcadas — e há ao menos uma. */
  isAllSelected: boolean
  isIndeterminate: boolean
  /** Não há nada para marcar nesta página: o "marcar todos" fica desabilitado. */
  isEmpty: boolean
  clear: () => void
}

export interface IUseRowSelectionOptions<TRow> {
  /** De onde sai o id da linha. Padrão: `row.id`. */
  getId?: (row: TRow) => number
  /** Linhas que não podem ser marcadas (um inscrito não pago, por exemplo). Padrão: todas podem. */
  isSelectable?: (row: TRow) => boolean
}

/**
 * Seleção de linhas de uma listagem: o estado, o "marcar todos" e a limpeza ao trocar de página.
 *
 * Eram **três telas, três modelos de dados e nenhum reuso**: `number[]` em Entrega de Trabalho e
 * em Base de Leads, `Set<number>` nos inscritos do Painel do Evento; checkbox `sm` magenta em
 * duas e `md` **roxo** na terceira; barra de ações em dois `ActionFab` empilhados, em um só, e
 * numa barra em linha acima da tabela.
 *
 * **A seleção é sempre da página que está na tela.** O hook guarda os ids marcados mas só
 * devolve os que estão nas `rows` de agora, e poda o estado quando as linhas mudam — que é
 * exatamente o defeito que Entrega de Trabalho tinha: marcar três linhas na página 1, ir para a
 * página 2 e abrir a exclusão em massa mandava ao diálogo (e ao endpoint) ids que já não estavam
 * na tela, com uma contagem que não batia com nada visível. Vir do hook é o que impede a volta
 * dele — não há mais um `setSelectedItems([])` para alguém esquecer de escrever no `loader`.
 *
 * **`toggleAll` tem a guarda de lista vazia.** Com a tabela vazia, `array.every(...)` é `true` e
 * o "marcar todos" aparecia **marcado** sobre uma tabela sem linha nenhuma; `isEmpty` também
 * desabilita o controle, porque não há o que marcar.
 */
export function useRowSelection<TRow>(rows: TRow[], options: IUseRowSelectionOptions<TRow> = {}): IRowSelection {
  const { getId = (row: TRow) => (row as { id: number }).id, isSelectable } = options
  const [selected, setSelected] = useState<Set<number>>(new Set())

  // Sem `useMemo` de propósito. Nada aqui depende da **identidade** deste array — só do
  // conteúdo dele, e a chave do efeito é a string abaixo. Memoizar por `[rows]` deixaria o
  // resultado velho quando o `isSelectable` de quem chama passasse a responder diferente para
  // as mesmas linhas (um `id` travado que veio do estado, por exemplo), e memoizar por
  // `[rows, getId, isSelectable]` recalcularia a cada render, porque os dois costumam chegar
  // como função literal. Um `filter` + `map` sobre uma página de listagem não é o custo desta
  // tela.
  const selectableIds = rows.filter((row) => (isSelectable ? isSelectable(row) : true)).map(getId)

  // Chave estável das linhas da página: é o que diz "a listagem mudou" sem disparar a cada
  // re-render por outro motivo (um filtro digitado, um modal aberto).
  const rowsKey = selectableIds.join(',')

  useEffect(() => {
    const visible = new Set(selectableIds)
    setSelected((prev) => {
      const next = new Set<number>()
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id)
      })
      return next.size === prev.size ? prev : next
    })
  }, [rowsKey])

  // Derivado, e não só podado no efeito: o efeito roda **depois da pintura**, e o quadro entre a
  // troca de página e ele é justamente onde a contagem errada aparecia.
  const selectedIds = selectableIds.filter((id) => selected.has(id))

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const isAllSelected = selectableIds.length > 0 && selectedIds.length === selectableIds.length

  return {
    selectedIds,
    count: selectedIds.length,
    isSelected: (id: number) => selected.has(id),
    toggle,
    toggleAll: () => setSelected(isAllSelected ? new Set() : new Set(selectableIds)),
    isAllSelected,
    isIndeterminate: selectedIds.length > 0 && !isAllSelected,
    isEmpty: selectableIds.length === 0,
    clear: () => setSelected(new Set())
  }
}
